import EventEmitter from 'eventemitter3'
import { NetLoader, Buffer, MSE, EVENT, StreamingError, BandwidthService, SeiService, GapService, isMediaPlaying, Logger, getVideoPlaybackQuality } from 'xgplayer-streaming-shared'
import { Logger as TransmuxerLogger } from 'xgplayer-transmuxer'
import { BufferService } from './services'
import { getOption } from './options'
import { searchKeyframeIndex } from './utils'

export const logger = new Logger('flv')

export class Flv extends EventEmitter {
  /** @type {HTMLMediaElement | null} */
  media = null

  _loading = false

  /** @type {import('./options').FlvOption} */
  _opts = null

  /** @type {BufferService | null} */
  _bufferService = null
  /** @type {GapService | null} */
  _gapService = null

  _mediaLoader = null
  _maxChunkWaitTimer = null

  _tickTimer = null
  _tickInterval = 500

  _noEndOfStreamOnDone = false
  _urlSwitching = false
  _seamlessSwitching = false

  _keyframes = null

  _acceptRanges = true

  /**
   * @param {import('./options').FlvOption} opts
   */
  constructor (opts) {
    super()
    this._opts = getOption(opts)
    this.media = this._opts.media || document.createElement('video')
    this._opts.media = null

    this._mediaLoader = new NetLoader({
      ...this._opts.fetchOptions,
      retry: this._opts.retryCount,
      retryDelay: this._opts.retryDelay,
      timeout: this._opts.loadTimeout,
      onRetryError: this._onRetryError,
      onProgress: this._onProgress,
      responseType: 'arraybuffer'
    })

    this._bufferService = new BufferService(this, this._opts.softDecode ? this.media : undefined, this._opts.analyzeDuration)
    this._seiService = new SeiService(this)
    this._bandwidthService = new BandwidthService()

    if (!this._opts.softDecode) {
      this._gapService = new GapService()
    }

    this.media.addEventListener('play', this._onPlay)
    this.media.addEventListener('seeking', this._onSeeking)
    this.media.addEventListener('timeupdate', this._onTimeupdate)

    this.on(EVENT.FLV_SCRIPT_DATA, this._onFlvScriptData)
  }

  get version () {
    return __VERSION__
  }

  get isLive () {
    return this._opts.isLive
  }

  get baseDts () {
    return this._bufferService?.baseDts
  }

  get seekable () {
    return !!this._keyframes && this._acceptRanges
  }

  speedInfo () {
    return {
      speed: this._bandwidthService.getLatestSpeed(),
      avgSpeed: this._bandwidthService.getAvgSpeed()
    }
  }

  bufferInfo (maxHole = 0.1) {
    return Buffer.info(Buffer.get(this.media), this.media?.currentTime, maxHole)
  }

  playbackQuality () {
    return getVideoPlaybackQuality(this.media)
  }

  /**
   * load or reload source
   * @param {string} [url]
   * @return {Promise}
   */
  async load (url, reuseMse = false) {
    if (!this._bufferService) return
    await this._reset(reuseMse)

    this._loadData(url)

    clearTimeout(this._tickTimer)
    this._tickTimer = setTimeout(this._tick, this._tickInterval)
  }

  /** @return {Promise} */
  async replay (seamlesslyReload = this._opts.seamlesslyReload, isPlayEmit) {
    if (!this.media) return
    if (seamlesslyReload) {
      this._noEndOfStreamOnDone = true
      await this._clear()

      setTimeout(() => {
        this._loadData(this._opts.url)
        this._bufferService.seamlessSwitch()
        this._seamlessSwitching = true
      })
    } else {
      await this.load()
    }
    return this.media.play(!isPlayEmit)
  }

  disconnect () {
    logger.debug('disconnect!')
    return this._clear()
  }

  /**
   * @param {string} url
   * @param {boolean} [seamless=false]
   */
  async switchURL (url, seamless) {
    if (!this._bufferService) return
    if (!seamless || !this._opts.isLive) {
      await this.load(url)
      this._urlSwitching = true
      return this.media.play(true)
    }

    this._noEndOfStreamOnDone = true
    await this._clear()

    setTimeout(() => {
      this._loadData(url)
      this._bufferService.seamlessSwitch()
      this._urlSwitching = true
      this._seamlessSwitching = true
    })
  }

  /** @return {Promise} */
  async destroy () {
    if (!this.media) return
    this.removeAllListeners()
    this._seiService.reset()
    this.media.removeEventListener('play', this._onPlay)
    this.media.removeEventListener('seeking', this._onSeeking)
    this.media.removeEventListener('timeupdate', this._onTimeupdate)
    await Promise.all([this._clear(), this._bufferService.destroy()])
    this.media = null
    this._bufferService = null
  }

  static isSupported () {
    // hack
    return MSE.isSupported() || !!WebAssembly
  }

  static enableLogger () {
    Logger.enable()
    TransmuxerLogger.enable()
  }

  static disableLogger () {
    Logger.disable()
    TransmuxerLogger.disable()
  }

  _emitError (error, endOfStream = true) {
    logger.table(error)
    logger.error(error)
    logger.error(this.media?.error)
    if (this._urlSwitching) {
      this._urlSwitching = false
      this._seamlessSwitching = false
      this.emit(EVENT.SWITCH_URL_FAILED, error)
    }
    this.emit(EVENT.ERROR, error)
    if (endOfStream) {
      this._seiService.reset()
      this._end()
    }
  }

  async _reset (reuseMse = false) {
    this._seiService.reset()
    this._bandwidthService.reset()
    await this._clear()
    await this._bufferService.reset(reuseMse)
  }

  async _loadData (url, range) {
    if (url) this._opts.url = url
    url = this._opts.url
    if (!url) throw new Error('Source url is missing')

    this.emit(EVENT.LOAD_START, { url })

    logger.debug('load data, loding:', this._loading, url)

    if (this._loading) {
      await this._mediaLoader.cancel()
    }

    this._loading = true
    try {
      const ret = await this._mediaLoader.load({ url, range })
      if (!ret) return
      const { response, firstByteTime, startTime } = ret
      const headers = response.headers
      this.emit(EVENT.TTFB, { url, responseUrl: response.url, elapsed: firstByteTime - startTime })
      this.emit(EVENT.LOAD_RESPONSE_HEADERS, { headers })
      this._acceptRanges = !!headers?.get('Accept-Ranges') || !!headers?.get('Content-Range')
    } catch (error) {
      return this._emitError(StreamingError.network(error))
    }
  }

  _onProgress = async (chunk, done, { startTime, endTime }) => {
    this._loading = !done

    if (!this._bufferService) return
    clearTimeout(this._maxChunkWaitTimer)

    this._bandwidthService.addChunkRecord(chunk?.byteLength, endTime - startTime)

    try {
      await this._bufferService.appendBuffer(chunk)
      if (this.isLive) {
        this._bufferService?.evictBuffer(this._opts.bufferBehind)
      }
    } catch (error) {
      return this._emitError(StreamingError.create(error))
    }

    if (this._urlSwitching) {
      this._urlSwitching = false
      this.emit(EVENT.SWITCH_URL_SUCCESS, { url: this._opts.url })
    }

    // ！！
    if (this._seamlessSwitching) {
      this._seamlessSwitching = false
      this.media.play()
      this._tick()
    }

    if (done && !this.media.seeking) {
      this.emit(EVENT.LOAD_COMPLETE)
      logger.debug('load done', this._noEndOfStreamOnDone)

      if (this._noEndOfStreamOnDone) {
        this._noEndOfStreamOnDone = false
      } else {
        return this._end()
      }
    } else {
      const { maxReaderInterval } = this._opts
      if (maxReaderInterval) {
        clearTimeout(this._maxChunkWaitTimer)
        this._maxChunkWaitTimer = setTimeout(() => {
          logger.debug('onMaxChunkWait', maxReaderInterval)
          this._end()
        }, maxReaderInterval)
      }
    }
  }

  _onRetryError = (error, retryTime) => {
    logger.debug('load retry', error, retryTime)

    this.emit(EVENT.LOAD_RETRY, {
      error: StreamingError.network(error),
      retryTime
    })
  }

  async _clear () {
    clearTimeout(this._maxChunkWaitTimer)
    clearTimeout(this._tickTimer)
    if (this._mediaLoader) await this._mediaLoader.cancel()
    this._loading = false
  }

  _end = () => {
    this._clear()
    if (this._bufferService) {
      this._bufferService.endOfStream()
    }

    logger.debug('end stream')
  }

  _tick = () => {
    clearTimeout(this._tickTimer)
    const { media } = this
    if (!media) return
    this._tickTimer = setTimeout(this._tick, this._tickInterval)

    const bufferEnd = Buffer.end(Buffer.get(media))

    if (bufferEnd < 0.1 || !media.readyState) return

    const opts = this._opts
    if (isMediaPlaying(media)) {
      if (this._gapService) {
        this._gapService.do(media, opts.maxJumpDistance, this.isLive, 3)
      }
    } else {
      if (!media.currentTime && this._gapService) {
        this._gapService.do(media, opts.maxJumpDistance, this.isLive, 3)
        return
      }
      if (opts.isLive && bufferEnd > opts.disconnectTime) {
        this.disconnect()
      }
    }
  }

  _onPlay = () => {
    const canReplay = this._opts.softDecode || this.media?.buffered?.length
    if (this.isLive && !this._loading && canReplay) {
      this.replay(undefined, true)
    }
  }

  _onSeeking = async () => {
    if (!this.isLive && this.seekable) {
      const times = this._keyframes.times
      const filepositions = this._keyframes.filepositions
      if (!times?.length || !filepositions?.length) return
      const currentTime = this.media.currentTime
      const i = searchKeyframeIndex(this._keyframes.times, currentTime)
      const startByte = filepositions[i]
      if (startByte === null || startByte === undefined) return
      await this._mediaLoader.cancel()
      this._loadData(null, [startByte])
      this._bufferService.unContiguous(times[i])
    }
  }

  _onTimeupdate = () => {
    if (!this.media) return

    const opts = this._opts
    const currentTime = this.media.currentTime

    if (opts.isLive && opts.maxLatency && opts.targetLatency) {
      const bufferEnd = Buffer.end(Buffer.get(this.media))
      const latency = bufferEnd - currentTime
      if (latency >= opts.maxLatency) {
        this.media.currentTime = bufferEnd - opts.targetLatency
      }
    }
    this._seiService.throw(currentTime)
  }

  _onFlvScriptData = (sample) => {
    const keyframes = sample.data?.onMetaData?.keyframes
    const duration = sample.data?.onMetaData?.duration
    if (keyframes) {
      this._keyframes = keyframes
    }

    if (!this._opts.isLive && duration) {
      this._bufferService.updateDuration(duration)
    }
  }
}

try {
  if (localStorage.getItem('xgd')) {
    Flv.enableLogger()
  } else {
    Flv.disableLogger()
  }
} catch (error) {
  // ignore
}
